import React, { useCallback, useMemo, useReducer, useState } from 'react';
import { Alert, Card, CardBody } from 'reactstrap';
import { useQuery } from '@tanstack/react-query';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  flexRender,
  getPaginationRowModel,
  SortingState,
} from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom';
import { RiFilter3Fill } from 'react-icons/ri';

import DebouncedInput from '@/components/DebounceInput';
import Button, { ButtonSizes, ButtonVariants } from '@/components/Button/Button';
import { getDepositsRest } from '@/services/deposits/deposits-service';
import SkeletonRow from '@/components/data-table/SkeletonRow';
import Paginator from '@/pages/common/paginator';
import SortableTableHeader from '@/components/data-table/SortableTableHeader';
import { convertSortingToApiFormat } from '@/common/utils/convert-sorting-to-api-format';

import { createColumns } from '../deposits.columns';
import { initialFilters, initialState, queryReducer } from '../deposits-query-reducer';
import DepositsFilterForm from './DepositsFilterForm';

const DepositsTable: React.FC = () => {
  const navigate = useNavigate();
  const [isDebouncing, setIsDebouncing] = useState<boolean>(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [queryParams, dispatch] = useReducer(queryReducer, initialState);

  const { sorting, ...rest } = queryParams;

  const { data, isLoading, error } = useQuery({
    queryKey: ['depositsData', queryParams],
    queryFn: () =>
      getDepositsRest({
        ...rest,
        sorts: convertSortingToApiFormat(sorting),
      }),
    staleTime: 60000,
  });

  const columns = useMemo(() => createColumns(navigate), [navigate]);

  const handleSortingChange = useCallback(
    (updater: SortingState | ((old: SortingState) => SortingState)) => {
      const newSorting = typeof updater === 'function' ? updater(sorting) : updater;
      dispatch({ type: 'SET_SORTING', payload: newSorting });
    },
    [sorting],
  );

  const tableInstance = useReactTable({
    data: data?.deposits || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: handleSortingChange,
    manualSorting: true,
    manualFiltering: true,
    manualPagination: true,
    state: {
      sorting: sorting,
    },
  });

  const handleSearch = useCallback((value: string | number) => {
    setIsDebouncing(true);
    dispatch({ type: 'SET_SEARCH', payload: value.toString() });
    setIsDebouncing(false);
  }, []);

  const handlePaginationChange = useCallback((skip: number, take: number) => {
    dispatch({ type: 'SET_PAGINATION', payload: { skip, take } });
  }, []);

  if (error) {
    return <Alert color="danger">Error when fetching deposits data</Alert>;
  }

  return (
    <div className="d-flex flex-column gap-3">
      <div className="w-100 d-flex justify-content-between align-items-center gap-2 py-2">
        <div className="d-flex align-items-center justify-content-start gap-2">
          <DebouncedInput
            className="form-control w-auto"
            placeholder="Search deposits..."
            onChange={handleSearch}
            value={queryParams.filter?.search || ''}
            isLoading={isDebouncing}
          />
          <Button
            onClick={() => setShowFilterModal(true)}
            className="btn btn-outline d-flex align-items-center justify-content-center gap-2"
            variant={ButtonVariants.secondary}
            size={ButtonSizes.sm}
          >
            <RiFilter3Fill size={20} />
            Filter
          </Button>
          {Object.values(queryParams.filter || {}).some(Boolean) && (
            <Button
              size={ButtonSizes.sm}
              variant={ButtonVariants.primary}
              onClick={() => dispatch({ type: 'RESET_FILTERS' })}
              className="btn btn-primary d-flex align-items-center justify-content-center gap-2"
            >
              Clear Filters
            </Button>
          )}
        </div>
      </div>

      <Card>
        <CardBody className="overflow-auto">
          <table className="table-hover table-centered table-nowrap deposits-table mb-0 table align-middle">
            <thead className="thead-light deposits-table__header">
              {tableInstance.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <SortableTableHeader key={header.id} header={header} />
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {isLoading
                ? Array.from({ length: queryParams.take || 25 }).map((_, index) => (
                    <SkeletonRow key={`skeleton-${index}`} columns={columns} />
                  ))
                : tableInstance.getRowModel().rows.map((row) => (
                    <tr key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                      ))}
                    </tr>
                  ))}
            </tbody>
          </table>
        </CardBody>
      </Card>

      <Paginator
        take={queryParams.take || 25}
        skip={queryParams.skip || 0}
        showing={data?.deposits.length || 0}
        totalCount={data?.totalCount || 0}
        setTake={(take) => handlePaginationChange(queryParams.skip || 0, take)}
        setSkip={(skip) => handlePaginationChange(skip, queryParams.take || 25)}
      />

      {showFilterModal && (
        <DepositsFilterForm
          onClose={() => setShowFilterModal(false)}
          onApplyFilters={(filters) => {
            dispatch({ type: 'SET_FILTERS', payload: filters });
            setShowFilterModal(false);
          }}
          currentFilters={queryParams.filter || initialFilters}
        />
      )}
    </div>
  );
};

export default DepositsTable;
